import { CommonModule } from '@angular/common';
import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, forkJoin } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { UtilitiesService } from '@nx-ahs/shared/services';
import {
	AppSpinnerComponent,
	ConfirmationModalService,
	DbIconComponent,
	ModalTemplateComponent
} from '@nx-ahs/shared/components';
import { FormErrorDirective, FormErrorListDirective } from '@nx-ahs/shared/directives';
import { GameService } from '../../../core/services/game.service';
import { KudoModel } from '../../../shared/models/kudo-to-send';

@Component({
	selector: 'app-send-kudos-dialog',
	templateUrl: './send-kudos-dialog.component.html',
	styleUrls: ['./send-kudos-dialog.component.scss'],
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatAutocompleteModule,
		MatFormFieldModule,
		MatInputModule,
		MatRadioModule,
		MatTooltipModule,
		AppSpinnerComponent,
		DbIconComponent,
		FormErrorDirective,
		FormErrorListDirective,
		ModalTemplateComponent
	]
})
export class SendKudosDialogComponent implements OnInit, OnDestroy {
	public filteredOptions: Observable<any[]>;
	public gameUsers = [];
	public kudosTypes = [];
	public loading = false;
	public saving: boolean;
	public sendKudosForm: FormGroup;
	public title = 'Send Kudos';

	constructor(
		public dialogRef: MatDialogRef<SendKudosDialogComponent>,
		private utilitiesService: UtilitiesService,
		private confirmationModalService: ConfirmationModalService,
		private formBuilder: FormBuilder,
		private gameService: GameService,
		private snackBar: MatSnackBar,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {}

	ngOnInit() {
		this.loadData();
		this.buildForm();

		this.filteredOptions = this.sendKudosForm.controls.kudosReceiver.valueChanges.pipe(
			startWith(''),
			map((user) => (typeof user === 'string' ? user : user ? user.firstName + ' ' + user.lastName : '')),
			map((name) => (name.length >= 2 ? this.filter(name) : []))
		);

		this.dialogRef.keydownEvents().subscribe((event) => {
			if (event.key === 'Escape') {
				this.cancel();
			}
		});
		this.dialogRef.backdropClick().subscribe(() => {
			this.cancel();
		});
	}

	ngOnDestroy() {
		this.sendKudosForm.reset();
	}

	private buildForm() {
		this.sendKudosForm = this.formBuilder.group({
			kudosReceiver: [null, Validators.required],
			kudosSelected: [null, Validators.required]
		});
	}

	public cancel(): void {
		this.confirmationModalService
			.showConfirmationDialog()
			.afterClosed()
			.toPromise()
			.then((result) => {
				if (result) {
					this.dialogRef.close(null);
				}
			});
	}

	public displayChoiceName(user) {
		return user ? user.firstName + ' ' + user.lastName : undefined;
	}

	private filter(name: string): string[] {
		const filterValue = name.toLowerCase();

		return this.gameUsers.filter((x) => {
			return (
				x.firstName.toLowerCase().startsWith(filterValue) || x.lastName.toLowerCase().startsWith(filterValue)
			);
		});
	}

	private loadData(): void {
		this.loading = true;
		const gameUsersObservable = this.gameService.getGameUsers();
		const kudoTypesObservable = this.gameService.getKudosTypes();

		forkJoin(gameUsersObservable, kudoTypesObservable)
			.toPromise()
			.then((results) => {
				// Process game users results
				this.gameUsers = results[0].filter((x) => {
					return x.userId != this.gameService.user.userId;
				});

				// Process kudo type results
				this.kudosTypes = results[1];
			})
			.finally(() => {
				this.loading = false;
			});
	}

	private processSaveError(formGroup: FormGroup, error: any): void {
		if (error.status == 422 && formGroup) {
			this.utilitiesService.setServerErrorsOnForm(formGroup, error.error);
		} else {
			throw error;
		}
	}

	public sendKudos(): void {
		// Mark all fields as touched so validation errors appear
		this.utilitiesService.markAllFieldsTouched(this.sendKudosForm);

		//if the form isn't complete, don't allow the user to send the kudo
		if (this.sendKudosForm.valid) {
			// Create kudo model to send to server from the form values
			const kudoToSend = new KudoModel(
				this.sendKudosForm.get('kudosReceiver').value.userId,
				this.sendKudosForm.get('kudosSelected').value
			);
			this.saving = true;

			// Save kudo
			this.gameService
				.sendKudos(kudoToSend)
				.toPromise()
				.then(() => {
					this.snackBar.open(
						`Kudos sent to ${this.displayChoiceName(this.sendKudosForm.get('kudosReceiver').value)}`,
						'DISMISS',
						{ duration: 5000 }
					);
					this.dialogRef.close(true);
				})
				.catch((error) => {
					this.processSaveError(this.sendKudosForm, error);
				})
				.finally(() => {
					this.saving = false;
				});
		}
	}

	public sendToBlur(): void {
		// Wrapped in a setTimeout due to weird timing issue with optionSelected handler on autocomplete.  Without setTimeout using the keyboard to
		// select an item fired before blur but using the mouse to select fired after blur.  The setTimeout allows this to function the same
		// regardless of which selection method is used.
		setTimeout(() => {
			if (!this.sendKudosForm.get('kudosReceiver').disabled) {
				this.sendKudosForm.get('kudosReceiver').setValue('');
			}
		}, 200);
	}

	public userSelected(): void {
		this.sendKudosForm.controls['kudosReceiver'].disable();
	}
}
